import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, ServerIcon, ClockIcon, SearchIcon, FileTextIcon, BarChartIcon, ZapIcon, PenToolIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { PricingCard } from '../components/pricing/PricingCard';
import { WebsiteCareComparisonTable } from '../components/pricing/WebsiteCareComparisonTable';
export function WebsiteCare() {
  const [tableExpanded, setTableExpanded] = useState(false);
  const toggleTable = () => {
    setTableExpanded(!tableExpanded);
  };
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const handleEssentialCareClick = () => {
    window.open('https://buy.stripe.com/14A8wJ1w0cICfXA10caZi06', '_blank');
  };
  const handleTotalGrowthClick = () => {
    window.open('https://buy.stripe.com/9B628l7UobEy9zcfV6aZi07', '_blank');
  };
  const handleOneOffFixClick = () => {
    window.open('https://buy.stripe.com/28EdR3fmQ8smaDgbEQaZi08', '_blank');
  };
  return <>
      <Helmet data-id="element-2357">
        <title data-id="element-2358">
          Website Maintenance Packages UK | Security, Updates, SEO & Support
        </title>
        <meta name="description" content="Keep your site fast and secure. Choose Essential (£79/month) or Total Growth (£299/month). One-off fixes £150. Hosting, SSL, backups and SEO included." data-id="element-2359" />
        <link rel="canonical" href="https://bright-pixxel.com/website-care" data-id="element-2360" />
        {/* Open Graph Meta Tags */}
        <meta property="og:locale" content="en_GB" data-id="element-2361" />
        <meta property="og:type" content="website" data-id="element-2362" />
        <meta property="og:title" content="Website Maintenance Packages UK | Security, Updates, SEO & Support" data-id="element-2363" />
        <meta property="og:description" content="Keep your site fast and secure. Choose Essential (£79/month) or Total Growth (£299/month). One-off fixes £150. Hosting, SSL, backups and SEO included." data-id="element-2364" />
        <meta property="og:url" content="https://bright-pixxel.com/website-care" data-id="element-2365" />
        <meta property="og:site_name" content="Bright Pixxel Studios" data-id="element-2366" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2367" />
        <meta property="og:image:width" content="1200" data-id="element-2368" />
        <meta property="og:image:height" content="630" data-id="element-2369" />
        <meta property="og:image:type" content="image/png" data-id="element-2370" />
        <meta property="og:image:alt" content="Bright Pixxel Studios - Website Maintenance Services" data-id="element-2371" />
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-2372" />
        <meta name="twitter:title" content="Website Maintenance Packages UK | Security, Updates, SEO & Support" data-id="element-2373" />
        <meta name="twitter:description" content="Keep your site fast and secure. Choose Essential (£79/month) or Total Growth (£299/month). One-off fixes £150. Hosting, SSL, backups and SEO included." data-id="element-2374" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" data-id="element-2375" />
        <meta name="twitter:image:alt" content="Bright Pixxel Studios - Website Maintenance Services" data-id="element-2376" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-[clamp(56px,8vw,72px)] pb-12 bg-gradient-to-b from-[#0C1B26] to-[#212C34]" data-id="element-2377">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2378">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2379">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl md:text-4xl font-bold mb-4 text-white" data-id="element-2380">
              Keep Your Website Secure, Fast & Up-to-Date
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-white/90 mb-8" data-id="element-2381">
              Professional, engaging content that connects with your audience
              and drives results. From website copy to blog posts, we create
              content that works.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Pricing Cards Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-2382">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2383">
          <div className="max-w-7xl mx-auto" data-id="element-2384">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2385">
              <PricingCard title="Essential Care" subtitle="Basic Maintenance (Monthly)" price="£79/month" popular={false} features={['Hosting & domain management', 'SSL renewal', 'Security monitoring', 'Monthly backups', 'Plugin/theme updates', 'Basic email support']} iconSrc="https://images.unsplash.com/photo-1633613286991-611fe299c4be?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Essential website care plan icon" ctaText="Get Started" onClick={handleEssentialCareClick} data-id="element-2386" />
              <PricingCard title="Total Growth" subtitle="Complete Solution (Monthly)" price="£299/month" popular={true} features={['All Essential Care features', 'Priority support', 'Ongoing SEO', 'Performance tuning', 'Monthly analytics report', 'Content updates (1 hr/m)']} iconSrc="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" iconAlt="ALT: Total Growth website care plan icon" ctaText="Most Popular" onClick={handleTotalGrowthClick} data-id="element-2387" />
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="relative rounded-xl overflow-hidden border border-[#E6E6E6] dark:border-[#333333] shadow-md" data-id="element-2388">
                <div className="p-6 bg-[#254055]" data-id="element-2389">
                  <div className="flex items-center mb-4" data-id="element-2390">
                    <div className="w-12 h-12 rounded-full overflow-hidden mr-4 flex-shrink-0" data-id="element-2391">
                      <img src="https://images.unsplash.com/photo-1581093450021-4a7360e9a6b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80" alt="ALT: One-off website fix plan icon" className="w-full h-full object-cover" data-id="element-2392" />
                    </div>
                    <div data-id="element-2393">
                      <h3 className="text-xl font-bold text-white" data-id="element-2394">
                        One-off Fix
                      </h3>
                      <p className="text-white/70 text-sm" data-id="element-2395">
                        Single Issue Resolution
                      </p>
                    </div>
                  </div>
                  <div className="mb-6" data-id="element-2396">
                    <span className="text-3xl font-bold text-[#0DC3DC]" data-id="element-2397">
                      £150
                    </span>
                    <span className="text-white/70 ml-1" data-id="element-2398">(Inc VAT)</span>
                  </div>
                  <ul className="space-y-3 mb-6" data-id="element-2399">
                    <li className="flex items-start" data-id="element-2400">
                      <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-2401" />
                      <span className="text-white/90" data-id="element-2402">Updates</span>
                    </li>
                    <li className="flex items-start" data-id="element-2403">
                      <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-2404" />
                      <span className="text-white/90" data-id="element-2405">Security check</span>
                    </li>
                    <li className="flex items-start" data-id="element-2406">
                      <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-2407" />
                      <span className="text-white/90" data-id="element-2408">Backup</span>
                    </li>
                    <li className="flex items-start" data-id="element-2409">
                      <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-2410" />
                      <span className="text-white/90" data-id="element-2411">Performance tweaks</span>
                    </li>
                    <li className="flex items-start" data-id="element-2412">
                      <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-2413" />
                      <span className="text-white/90" data-id="element-2414">30-day support</span>
                    </li>
                    <li className="flex items-start" data-id="element-2415">
                      <CheckIcon size={18} className="text-[#0DC3DC] mr-2 mt-0.5 flex-shrink-0" data-id="element-2416" />
                      <span className="text-white/90" data-id="element-2417">
                        No ongoing commitment
                      </span>
                    </li>
                  </ul>
                  <button onClick={handleOneOffFixClick} className="w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 bg-white/10 hover:bg-white/20 text-white border border-white/20" data-id="element-2418">
                    Get Started
                  </button>
                </div>
              </motion.div>
            </div>
            <div className="mt-12 text-center" data-id="element-2419">
              <button onClick={toggleTable} className="inline-flex items-center justify-center mx-auto px-8 py-3 rounded-full font-medium text-[15px] text-[#0DC3DC] bg-transparent border border-[#0DC3DC] border-opacity-40 hover:bg-gradient-to-r hover:from-[#0DC3DC] hover:to-[#00A8BF] hover:text-white hover:border-opacity-0 transition-all duration-300 shadow-sm hover:shadow-lg" style={{
              fontFamily: 'Poppins, sans-serif',
              minHeight: '46px'
            }} data-id="element-2420">
                <span className="mr-2" data-id="element-2421">
                  {tableExpanded ? 'Hide detailed comparison' : 'Show detailed comparison'}
                </span>
                {tableExpanded ? <ChevronUpIcon size={16} data-id="element-2422" /> : <ChevronDownIcon size={16} data-id="element-2423" />}
              </button>
            </div>
            {/* Comparison Table */}
            {tableExpanded && <WebsiteCareComparisonTable data-id="element-2424" />}
          </div>
        </div>
      </section>
      {/* Features Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2425">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2426">
          <div className="max-w-4xl mx-auto" data-id="element-2427">
            <div className="text-center mb-12" data-id="element-2428">
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2429">
                What's Included in Your Care Plan
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-2430">
                All our care plans include these essential features to keep your
                website running smoothly.
              </motion.p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2431">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2432">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2433">
                  <ServerIcon size={24} className="text-[#0DC3DC]" data-id="element-2434" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2435">Hosting</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2436">
                  Reliable hosting with SSL certificates and domain management
                  included.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2437">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2438">
                  <ShieldCheckIcon size={24} className="text-[#0DC3DC]" data-id="element-2439" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2440">Security</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2441">
                  24/7 monitoring, malware scanning, and regular security
                  updates.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2442">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2443">
                  <SearchIcon size={24} className="text-[#0DC3DC]" data-id="element-2444" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white" data-id="element-2445">SEO</h3>
                <p className="text-white/90" data-id="element-2446">
                  Ongoing optimisation to maintain and improve search rankings.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2447">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2448">
                  <FileTextIcon size={24} className="text-[#0DC3DC]" data-id="element-2449" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2450">Content</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2451">
                  Regular content updates to keep your site fresh and relevant.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2452">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2453">
                  <BarChartIcon size={24} className="text-[#0DC3DC]" data-id="element-2454" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2455">Reports</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2456">
                  Monthly analytics reports to track your site's performance.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.6
            }} className="bg-white dark:bg-[#1E1E1E] p-6 rounded-xl shadow-md text-center" data-id="element-2457">
                <div className="w-12 h-12 mx-auto bg-[#0DC3DC]/20 rounded-lg flex items-center justify-center mb-4" data-id="element-2458">
                  <ClockIcon size={24} className="text-[#0DC3DC]" data-id="element-2459" />
                </div>
                <h3 className="text-lg font-semibold mb-2" data-id="element-2460">Support</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm" data-id="element-2461">
                  Priority email and phone support when you need assistance.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* Trust Section */}
      <section className="py-12 bg-white dark:bg-[#1E1E1E]" data-id="element-2462">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2463">
          <div className="max-w-4xl mx-auto" data-id="element-2464">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-[#212C34] p-8 rounded-xl shadow-md text-center" data-id="element-2465">
              <h3 className="text-2xl font-bold mb-4 text-white" data-id="element-2466">
                Your Website, Your Control
              </h3>
              <p className="text-white/90 text-lg mb-6" data-id="element-2467">
                Full ownership of your site + 3 months free hosting + free
                domain & SSL
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2468">
                <div className="flex items-center justify-center" data-id="element-2469">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-2470" />
                  <span className="text-white/90" data-id="element-2471">100% Site Ownership</span>
                </div>
                <div className="flex items-center justify-center" data-id="element-2472">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-2473" />
                  <span className="text-white/90" data-id="element-2474">3 Months Free Hosting</span>
                </div>
                <div className="flex items-center justify-center" data-id="element-2475">
                  <CheckIcon size={20} className="text-[#0DC3DC] mr-2 flex-shrink-0" data-id="element-2476" />
                  <span className="text-white/90" data-id="element-2477">Free Domain & SSL</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-2478">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2479">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2480">
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="mb-8" data-id="element-2481">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/qh74mEBb962nRk6qjvv9uT/Geometric_Logo_of_Bright_Pixxel_Studio.png" alt="Bright Pixxel Studio - Affordable, Creative, Digital Excellence" className="h-16 mx-auto" data-id="element-2482" />
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-3xl font-bold mb-4" data-id="element-2483">
              Ready to protect your website investment?
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-gray-600 dark:text-gray-300 mb-8" data-id="element-2484">
              Get in touch today to discuss your website maintenance needs.
              We'll help you choose the right plan to keep your site secure,
              fast, and performing at its best.
            </motion.p>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-2485">
              <Button size="lg" onClick={scrollToContact} data-id="element-2486">
                Start Your Care Plan
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </>;
}